import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ChangePassword() {
  const { setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) return toast.error("New passwords do not match");
    if (form.new_password.length < 6) return toast.error("Password must be at least 6 characters");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: form.current_password, new_password: form.new_password,
      });
      setUser(data);
      toast.success("Password updated");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6 grid-bg">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl border border-slate-100 shadow-xl p-8 animate-fade-up" data-testid="change-password-form">
        <Logo />
        <h1 className="font-display text-2xl font-extrabold mt-6">Set a new password</h1>
        <p className="text-slate-500 text-sm mt-1 mb-6">For security, please change your temporary password before continuing.</p>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Current / temporary password</Label>
            <Input data-testid="current-password-input" type="password" required value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="mt-1.5 h-11" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">New password</Label>
            <Input data-testid="new-password-input" type="password" required minLength={6} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="mt-1.5 h-11" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Confirm new password</Label>
            <Input data-testid="confirm-password-input" type="password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="mt-1.5 h-11" />
          </div>
        </div>
        <Button type="submit" disabled={loading} data-testid="change-password-submit" className="w-full mt-6 h-11 bg-sky-600 hover:bg-sky-700 rounded-xl font-semibold">{loading ? "Saving…" : "Update password"}</Button>
        <button type="button" onClick={doLogout} data-testid="change-password-logout" className="w-full mt-3 text-sm text-slate-500 hover:text-slate-800">Sign out</button>
      </form>
    </div>
  );
}
